import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import styles from '../DescriptionEditor.module.scss';
import useUiMotion from '../hooks/useUiMotion';

/**
 * @-mention dropdown. Portaled to document.body and positioned relative to
 * TipTap's `clientRect` for the current mention trigger, flipping above the
 * caret when there isn't room below.
 *
 * Exposes an imperative `handleKeyDown` so the parent can forward TipTap's
 * suggestion key events without reaching into refs.
 */
const MentionList = forwardRef(({ items, query, clientRect, onSelect }, ref) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);
  const uiMotion = useUiMotion();

  useImperativeHandle(ref, () => ({
    handleKeyDown({ event }) {
      if (event.key === 'ArrowDown') {
        setActiveIndex((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
        return true;
      }
      if (event.key === 'ArrowUp') {
        setActiveIndex((i) => Math.max(i - 1, 0));
        return true;
      }
      if (event.key === 'Enter') {
        if (items[activeIndex]) {
          onSelect(items[activeIndex]);
          return true;
        }
      }
      if (event.key === 'Escape') return true;
      return false;
    },
  }));

  useEffect(() => {
    setActiveIndex(0);
  }, [items]);

  useEffect(() => {
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const rect = clientRect?.();
  if (!rect) return null;

  const GAP = 6;
  const MAXH = 240;
  const MINW = 220;
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const openDown = spaceBelow >= MAXH || spaceBelow >= spaceAbove;
  const spaceRight = window.innerWidth - rect.left;
  const alignLeft = spaceRight >= MINW;

  const posStyle = {
    position: 'fixed',
    zIndex: 9999,
    minWidth: MINW,
    maxWidth: Math.min(400, window.innerWidth - 16),
    ...(openDown
      ? { top: rect.bottom + GAP, maxHeight: Math.min(MAXH, spaceBelow - GAP) }
      : {
          bottom: window.innerHeight - rect.top + GAP,
          maxHeight: Math.min(MAXH, spaceAbove - GAP),
        }),
    ...(alignLeft
      ? { left: rect.left }
      : { right: window.innerWidth - rect.right }),
  };

  return createPortal(
    <motion.div
      className={`${styles.dropdown} ${styles.dropdownMotion}`}
      style={posStyle}
      initial={{ opacity: 0, y: openDown ? -8 : 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: openDown ? -5 : 5, scale: 0.99 }}
      transition={uiMotion.pop}
    >
      {!query ? (
        <div className={styles.dropdownPlaceholder}>Start typing to find users</div>
      ) : items.length === 0 ? (
        <div className={styles.dropdownPlaceholder}>No users found</div>
      ) : (
        <ul ref={listRef} className={styles.dropdownList} role="listbox">
          {items.map((user, i) => (
            <li
              key={user.uid}
              role="option"
              aria-selected={i === activeIndex}
              className={`${styles.dropdownItem} ${
                i === activeIndex ? styles.dropdownItemActive : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(user);
              }}
            >
              <img
                className={styles.dropdownAvatar}
                src={user.avatar || './avatar.png'}
                alt=""
                aria-hidden
              />
              <div className={styles.dropdownInfo}>
                <span className={styles.dropdownDisplayName}>
                  {user.displayName || user.userName}
                </span>
                <span className={styles.dropdownHandle}>@{user.handle}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </motion.div>,
    document.body
  );
});

MentionList.displayName = 'MentionList';

export default MentionList;
